import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout, { Container } from "../components/layout";
import axios from "axios";
import { apiurl } from "../config/config";
import { SuwasthiLoader } from "../components/loader";
import Breadcrumb from "../components/breadcrumb";
import {
  BadgeCheck,
  Contact,
  CreditCard,
  FileBadge,
  Home,
  IdCard,
  KeyRound,
  Landmark,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  User,
  Users,
  CalendarCheck,
  Sun,
  BedDouble,
  Umbrella,
} from "lucide-react";
import { UserPlus, Calendar, Briefcase, Heart } from "lucide-react";
import { BsPassport } from "react-icons/bs";
import {
  FileText,
  FileCheck,
  FileX,
  LoaderCircle,
  ExternalLink,
} from "lucide-react";
import { TbFileAlert } from "react-icons/tb";
import { Banknote, Hash, FileCheck2, ShieldX, Clock } from "lucide-react";

const EmployeeDetails = () => {
  const { employeeObjId } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [documentLink, setDocumentLink] = useState("");
  const [show, setShow] = useState(false);
  const [url, setUrl] = useState("");
  const [showAddAppModal, setShowAddAppModal] = useState(false);

  useEffect(() => {
    axios
      .get(`${apiurl}/api/employee/get/${employeeObjId}`)
      .then((res) => {
        setEmployee(res?.data?.employee);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching employee:", error);
        setLoading(false);
      });
  }, [employeeObjId]);

  const openModal = (link) => {
    setDocumentLink(link);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setDocumentLink("");
  };

  const getStatusBadge = (status) => {
    const base = "text-xs font-medium px-2 py-1 rounded-full";
    switch (status) {
      case "verified":
        return (
          <span className={`${base} bg-green-100 text-green-700`}>
            Verified
          </span>
        );
      case "rejected":
        return (
          <span className={`${base} bg-red-100 text-red-700`}>Rejected</span>
        );
      case "pending":
      default:
        return (
          <span className={`${base} bg-yellow-100 text-yellow-700`}>
            Pending
          </span>
        );
    }
  };

  const getIcon = (key) => {
    switch (key) {
      case "aadharCard":
      case "panCard":
      case "passport":
        return <FileText className="w-5 h-5 text-blue-600" />;
      case "photo":
      case "passportSizePhotos":
        return <FileCheck className="w-5 h-5 text-pink-600" />;
      case "resume":
        return <FileText className="w-5 h-5 text-purple-600" />;
      case "cancelledChequeOrPassbook":
        return <FileText className="w-5 h-5 text-cyan-600" />;
      default:
        return <LoaderCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const renderValue = (value) => value || "NA";

  if (loading) {
    return (
      <Layout>
        <SuwasthiLoader />
      </Layout>
    );
  }

  if (!employee) {
    return (
      <Layout>
        <div className="text-center text-red-500 font-semibold mt-10">
          Employee not found.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Breadcrumb page="Employee Details" />
      {/* <div className="flex justify-between items-center mb-4">
        <Link className="flex items-center gap-2 px-4 py-2 rounded-lg text-white bg-blue-500 hover:bg-blue-600 transition" to={`/employees/${employeeObjId}/manage`}>Manage</Link>
      </div> */}
      <Container overflow="overflow-scroll">
        <div className="p-4 bg-gray-50 min-h-screen mb-10">
          <div className="bg-white rounded-2xl shadow-sm border p-6 w-full mb-4 mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center gap-6 mb-6">
              <img
                src={employee?.documents?.photo?.link || "/images/user.png"}
                alt={
                  employee?.personalDetails?.firstName +
                  " " +
                  employee?.personalDetails?.lastName
                }
                className="w-24 h-24 rounded-full object-cover border-2 border-gray-300 p-1"
              />
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-800 capitalize">
                  {`${employee?.personalDetails?.firstName || ""} ${
                    employee?.personalDetails?.lastName || ""
                  }`.trim() || "N/A"}
                </h2>
                <p className="text-sm text-gray-500">
                  Employee Code:{" "}
                  <span className="font-medium">
                    {employee?.employeeCode || "N/A"}
                  </span>
                </p>
                <p className="text-sm text-gray-500">
                  Phone:{" "}
                  <span className="font-medium">
                    {employee?.personalDetails?.phone || "N/A"}
                  </span>
                </p>
                <p className="text-sm text-gray-500">
                  Email:{" "}
                  <span className="font-medium">
                    {employee?.personalDetails?.email || "N/A"}
                  </span>
                </p>
              </div>

              {/* Application Link Button */}
              {employee?.applicationDetails?.applicationObjId && (
                <button
                  onClick={() => {
                    setUrl(
                      `/nolayout/applications/${employee?.applicationDetails?.applicationObjId}/manage`
                    );
                    setShow(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-xs transition"
                >
                  Open Here
                </button>
              )}
              {show && (
                <PopupWithUrl
                  url={url}
                  onClose={() => setShow(false)}
                  applicationObjId={
                    employee?.applicationDetails?.applicationObjId
                  }
                />
              )}

              {/* Status Badge */}
              <div className="sm:ml-auto">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    employee?.employeeStatus?.toLowerCase() === "active"
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {employee?.employeeStatus || "N/A"}
                </span>
              </div>
              <Link
                to={`/employees/${employee?._id}/manage`}
                className="text-blue-600 button border px-3 py-1 rounded"
              >
                Manage
              </Link>
            </div>

            {/* Grid Info Section */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm text-gray-700">
              <p>
                <strong>ID:</strong> {renderValue(employee?.id)}
              </p>
              <p>
                <strong>Type:</strong> {renderValue(employee?.employeeType)}
              </p>
              <p>
                <strong>Department:</strong> {renderValue(employee?.department)}
              </p>
              <p>
                <strong>Designation:</strong>{" "}
                {renderValue(employee?.designation)}
              </p>
              <p>
                <strong>Office Location:</strong>{" "}
                {renderValue(employee?.officeLocation)}
              </p>
              <p>
                <strong>Joining Date:</strong>{" "}
                {employee?.employeeJoiningDate
                  ? new Date(employee.employeeJoiningDate).toLocaleDateString()
                  : "NA"}
              </p>
              <p>
                <strong>Leaving Date:</strong>{" "}
                {employee?.employeeLeavingDate
                  ? new Date(employee.employeeLeavingDate).toLocaleDateString()
                  : "NA"}
              </p>
              <p>
                <strong>Gender:</strong>{" "}
                {renderValue(employee?.personalDetails?.gender)}
              </p>
              <p>
                <strong>Marital Status:</strong>{" "}
                {renderValue(employee?.personalDetails?.maritalStatus)}
              </p>
              <p>
                <strong>Date of Birth:</strong>{" "}
                {employee?.personalDetails?.dateOfBirth
                  ? new Date(
                      employee.personalDetails.dateOfBirth
                    ).toLocaleDateString()
                  : "NA"}
              </p>
              <p>
                <strong>Blood Group:</strong>{" "}
                {renderValue(employee?.personalDetails?.bloodGroup)}
              </p>
              <p>
                <strong>Nationality:</strong>{" "}
                {renderValue(employee?.personalDetails?.nationality)}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6 w-full mx-auto mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-10">
              {/* Current Address */}
              <div>
                <h4 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <Home /> <span className="ml-2">Current Address</span>
                </h4>
                <div className="text-sm text-gray-700 space-y-2">
                  <p>
                    <strong>Type:</strong>{" "}
                    {renderValue(
                      employee?.personalDetails?.currentAddress?.type
                    )}
                  </p>
                  <p>
                    <strong>Address:</strong>
                    <br />
                    {renderValue(
                      employee?.personalDetails?.currentAddress?.address1
                    )}
                    <br />
                    {renderValue(
                      employee?.personalDetails?.currentAddress?.address2
                    )}
                  </p>
                  <p>
                    <strong>Landmark:</strong>{" "}
                    {renderValue(
                      employee?.personalDetails?.currentAddress?.landmark
                    )}
                  </p>
                  <p>
                    <strong>City:</strong>{" "}
                    {renderValue(
                      employee?.personalDetails?.currentAddress?.city
                    )}
                  </p>
                  <p>
                    <strong>Province:</strong>{" "}
                    {renderValue(
                      employee?.personalDetails?.currentAddress?.province
                    )}
                  </p>
                  <p>
                    <strong>Zip Code:</strong>{" "}
                    {renderValue(
                      employee?.personalDetails?.currentAddress?.zipCode
                    )}
                  </p>
                  <p>
                    <strong>Country:</strong>{" "}
                    {renderValue(
                      employee?.personalDetails?.currentAddress?.country
                    )}
                  </p>
                </div>
              </div>

              {/* Permanent Address */}
              <div>
                <h4 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <Home /> <span className="ml-2">Permanent Address</span>
                </h4>
                <div className="text-sm text-gray-700 space-y-2">
                  <p>
                    <strong>Type:</strong>{" "}
                    {renderValue(
                      employee?.personalDetails?.permanentAddress?.type
                    )}
                  </p>
                  <p>
                    <strong>Address:</strong>
                    <br />
                    {renderValue(
                      employee?.personalDetails?.permanentAddress?.address1
                    )}
                    <br />
                    {renderValue(
                      employee?.personalDetails?.permanentAddress?.address2
                    )}
                  </p>
                  <p>
                    <strong>Landmark:</strong>{" "}
                    {renderValue(
                      employee?.personalDetails?.permanentAddress?.landmark
                    )}
                  </p>
                  <p>
                    <strong>City:</strong>{" "}
                    {renderValue(
                      employee?.personalDetails?.permanentAddress?.city
                    )}
                  </p>
                  <p>
                    <strong>Province:</strong>{" "}
                    {renderValue(
                      employee?.personalDetails?.permanentAddress?.province
                    )}
                  </p>
                  <p>
                    <strong>Zip Code:</strong>{" "}
                    {renderValue(
                      employee?.personalDetails?.permanentAddress?.zipCode
                    )}
                  </p>
                  <p>
                    <strong>Country:</strong>{" "}
                    {renderValue(
                      employee?.personalDetails?.permanentAddress?.country
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border rounded-xl p-6 mb-6 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold mb-4">Reporting Details</h2>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <p>
                <strong>Manager Name:</strong>{" "}
                {renderValue(employee?.reportingDetails?.managerName)}
              </p>
              <p>
                <strong>Manager Employee Code:</strong>
                {renderValue(employee?.reportingDetails?.managerEmployeeCode)}
              </p>
              {/* <p>
                <strong>Type:</strong> {renderValue(employee.employeeType)}
              </p> */}
              <p>
                <strong>Department:</strong> {renderValue(employee.department)}
              </p>
              <p>
                <strong>Designation:</strong>{" "}
                {renderValue(employee.designation)}
              </p>
              <p>
                <strong>Office Location:</strong>{" "}
                {renderValue(employee.officeLocation)}
              </p>
              <p>
                <strong>Joining Date:</strong>{" "}
                {employee.employeeJoiningDate
                  ? new Date(employee.employeeJoiningDate).toLocaleDateString()
                  : "NA"}
              </p>
              {/* <p>
                <strong>Leaving Date:</strong>{" "}
                {employee.employeeLeavingDate
                  ? new Date(employee.employeeLeavingDate).toLocaleDateString()
                  : "NA"}
              </p> */}
            </div>
          </div>

          <div className="bg-white border rounded-xl p-6 mb-6 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold mb-4">Salary Details</h2>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <p>
                <strong>Work Schedule</strong>{" "}
                {renderValue(employee?.offerDetails.workSchedule)}
              </p>
              <p>
                <strong>Monthly Salary:</strong>
                {renderValue(
                  employee?.offerDetails?.salaryDetails.monthlySalary
                )}
              </p>
            </div>
          </div>

          {/* <div className="bg-white border rounded-xl p-6 mb-6 shadow-sm">
            <h2 className="text-xl font-bold mb-4">Bank Details</h2>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(employee.bankDetails || {}).map(
                ([key, value]) => (
                  <p key={key}>
                    <strong>{key}:</strong> {renderValue(value)}
                  </p>
                )
              )}
            </div>
          </div> */}
          <BankDetailsCard bankDetails={employee.bankDetails} />
          <FamilyDetailsCard familyDetails={employee.familyDetails} />

          {/* <div className="bg-white border rounded-xl p-6 mb-6 shadow-sm">
            <h2 className="text-xl font-bold mb-4">Family Details</h2>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(employee.familyDetails || {}).map(
                ([key, value]) => (
                  <p key={key}>
                    <strong>{key}:</strong> {renderValue(value)}
                  </p>
                )
              )}
            </div>
          </div> */}

          {/* <div className="bg-white border rounded-xl p-6 mb-6 shadow-sm">
            <h2 className="text-xl font-bold mb-4">Emergency Contact</h2>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(employee.emergencyContactDetails || {}).map(
                ([key, value]) => (
                  <p key={key}>
                    <strong>{key}:</strong> {renderValue(value)}
                  </p>
                )
              )}
            </div>
          </div> */}
          <EmergencyContactCard employee={employee} />

          {/* <div className="bg-white border rounded-xl p-6 mb-6 shadow-sm">
            <h2 className="text-xl font-bold mb-4">Leave Balance</h2>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(employee.leaveBalance || {}).map(
                ([key, value]) => (
                  <p key={key}>
                    <strong>{key}:</strong> {renderValue(value)}
                  </p>
                )
              )}
            </div>
          </div> */}

          <LeaveBalanceCard leaveBalance={employee.leaveBalance} />

          {/* <div className="bg-white border rounded-xl p-6 mb-6 shadow-sm">
            <h2 className="text-xl font-bold mb-4">Documents</h2>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(employee.documents || {}).map(([key, doc]) => (
                <p key={key}>
                  <strong>{key}:</strong>
                  {doc?.uploaded ? (
                    <button
                      className="text-blue-600 underline ml-2"
                      onClick={() => openModal(doc.link)}
                    >
                      View
                    </button>
                  ) : (
                    " NA"
                  )}
                </p>
              ))}
            </div>
          </div> */}

          <div className="bg-white border rounded-2xl p-6 mb-6 shadow-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <FileText className="w-6 h-6 text-indigo-600" />
                Documents
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Object.entries(employee?.documents || {})
                .filter(([key]) => !Array.isArray(employee.documents[key])) // exclude array types for now
                .map(([key, doc]) => (
                  <div
                    key={key}
                    className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg border hover:shadow-md transition duration-300"
                  >
                    <div className="mt-1">{getIcon(key)}</div>
                    <div>
                      <p className="text-sm text-gray-600 capitalize">
                        {key.replace(/([A-Z])/g, " $1")}
                      </p>
                      {doc?.uploaded ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openModal(doc.link)}
                            className="text-blue-600 underline text-sm flex items-center gap-1"
                          >
                            View <ExternalLink className="w-4 h-4" />
                          </button>
                          {getStatusBadge(doc.verificationStatus)}
                        </div>
                      ) : (
                        <p className="text-gray-400 text-sm italic">
                          Not uploaded
                        </p>
                      )}
                      {doc?.verificationRemarks && (
                        <p className="text-xs mt-1 text-red-500 italic">
                          Remark: {doc.verificationRemarks}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Object.entries(employee?.educationalCertificates || {})
                .filter(([key]) => !Array.isArray(employee.educationalCertificates[key])) // exclude array types for now
                .map(([key, doc]) => (
                  <div
                    key={key}
                    className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg border hover:shadow-md transition duration-300"
                  >
                    <div className="mt-1">{getIcon(key)}</div>
                    <div>
                      <p className="text-sm text-gray-600 capitalize">
                        {/* {key.replace(/([A-Z])/g, " $1")} */}
                        {doc?.name}
                      </p>
                      {doc?.uploaded ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openModal(doc.link)}
                            className="text-blue-600 underline text-sm flex items-center gap-1"
                          >
                            View <ExternalLink className="w-4 h-4" />
                          </button>
                          {getStatusBadge(doc.verificationStatus)}
                        </div>
                      ) : (
                        <p className="text-gray-400 text-sm italic">
                          Not uploaded
                        </p>
                      )}
                      {doc?.verificationRemarks && (
                        <p className="text-xs mt-1 text-red-500 italic">
                          Remark: {doc.verificationRemarks}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div className="bg-white border rounded-2xl p-6 mb-6 shadow-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <IdCard className="w-6 h-6 text-blue-600" />
                Identification Details
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Field cards */}
              {[
                {
                  label: "Aadhar Number",
                  value: employee?.identification?.aadharNumber,
                  icon: <IdCard className="text-indigo-600 w-5 h-5" />,
                },
                {
                  label: "PAN Number",
                  value: employee?.identification?.panNumber,
                  icon: <CreditCard className="text-green-600 w-5 h-5" />,
                },
                {
                  label: "Passport Number",
                  value: employee?.identification?.passportNumber,
                  icon: <BsPassport className="text-red-600 w-5 h-5" />,
                },
                {
                  label: "Voter ID",
                  value: employee?.identification?.voterId,
                  icon: <FileBadge className="text-yellow-600 w-5 h-5" />,
                },
                {
                  label: "Driving License",
                  value: employee?.identification?.drivingLicense,
                  icon: <BadgeCheck className="text-pink-600 w-5 h-5" />,
                },
                {
                  label: "UAN Number",
                  value: employee?.identification?.uanNumber,
                  icon: <KeyRound className="text-purple-600 w-5 h-5" />,
                },
                {
                  label: "ESIC Number",
                  value: employee?.identification?.esicNumber,
                  icon: <ShieldCheck className="text-teal-600 w-5 h-5" />,
                },
                {
                  label: "Gratuity Number",
                  value: employee?.identification?.gratuityNumber,
                  icon: <Landmark className="text-orange-600 w-5 h-5" />,
                },
                {
                  label: "Shops & Establishment Reg.",
                  value: employee?.identification?.shopsEstablishmentReg,
                  icon: <Contact className="text-rose-600 w-5 h-5" />,
                },
              ].map(({ label, value, icon }) => (
                <div
                  key={label}
                  className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg border hover:shadow-md transition duration-300 ease-in-out"
                >
                  <div className="mt-1">{icon}</div>
                  <div>
                    <p className="text-sm text-gray-600">{label}</p>
                    <p className="text-base font-semibold text-gray-800">
                      {renderValue(value)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {showModal && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm z-50">
              <div className="bg-white rounded-lg shadow-lg p-6 max-w-3xl w-full">
                <h2 className="text-xl font-semibold mb-4">Document Preview</h2>
                {documentLink.endsWith(".pdf") ? (
                  <iframe
                    src={documentLink}
                    className="w-full h-[500px]"
                    title="PDF Document"
                  />
                ) : (
                  <img
                    src={documentLink}
                    alt="Document"
                    className="w-full max-h-[500px] object-contain rounded-lg"
                  />
                )}
                <div className="mt-6 flex justify-end gap-4">
                  <a
                    href={documentLink}
                    target="_blank"
                    download
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 shadow"
                  >
                    Download
                  </a>
                  <button
                    onClick={closeModal}
                    className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 shadow"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Container>
    </Layout>
  );
};

export default EmployeeDetails;

const EditModal = ({ section, data, onClose, onSave }) => {
  const [formData, setFormData] = useState(data || {});

  const handleChange = (key, value) => {
    setFormData({ ...formData, [key]: value });
  };

  const handleSubmit = () => {
    onSave(section, formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-xl w-full max-w-2xl p-6 shadow-lg animate-fade-in">
        <h2 className="text-lg font-semibold mb-4 capitalize">
          Edit {section}
        </h2>
        <div className="grid grid-cols-2 gap-4">
          {Object.entries(formData).map(([key, value]) => (
            <div key={key}>
              <label className="block text-sm font-medium mb-1 capitalize">
                {key}
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={value}
                onChange={(e) => handleChange(key, e.target.value)}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button
            className="bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded-lg"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            onClick={handleSubmit}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

const EmergencyContactCard = ({ employee }) => {
  const renderValue = (value) => value || "NA";

  const renderEmergencyLabel = (key) => {
    switch (key) {
      case "emergencyContactName":
        return {
          label: "Name",
          icon: <User className="w-5 h-5 text-indigo-500" />,
        };
      case "emergencyContactRelationship":
        return {
          label: "Relationship",
          icon: <Users className="w-5 h-5 text-green-600" />,
        };
      case "emergencyContactPhone":
        return {
          label: "Phone Number",
          icon: <Phone className="w-5 h-5 text-blue-600" />,
        };
      case "emergencyContactEmail":
        return {
          label: "Email",
          icon: <Mail className="w-5 h-5 text-orange-500" />,
        };
      case "emergencyContactAddress":
        return {
          label: "Address",
          icon: <MapPin className="w-5 h-5 text-purple-500" />,
        };
      case "verificationRemarks":
        return {
          label: "Remarks",
          icon: <TbFileAlert className="w-5 h-5 text-red-500" />,
        };
      case "verificationStatus":
        return {
          label: "Verification",
          icon: <ShieldCheck className="w-5 h-5 text-yellow-600" />,
        };
      default:
        return { label: key, icon: null };
    }
  };

  const getStatusBadge = (status) => {
    const base = "text-xs font-medium px-2 py-1 rounded-full";
    switch (status) {
      case "verified":
        return (
          <span className={`${base} bg-green-100 text-green-700`}>
            Verified
          </span>
        );
      case "rejected":
        return (
          <span className={`${base} bg-red-100 text-red-700`}>Rejected</span>
        );
      default:
        return (
          <span className={`${base} bg-yellow-100 text-yellow-700`}>
            Pending
          </span>
        );
    }
  };
  return (
    <div className="bg-white border rounded-2xl p-6 mb-6 shadow-md">
      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <Users className="w-6 h-6 text-indigo-600" />
        Emergency Contact
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {Object.entries(employee.emergencyContactDetails || {}).map(
          ([key, value]) => {
            const { label, icon } = renderEmergencyLabel(key);
            return (
              <div
                key={key}
                className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg border hover:shadow-md transition"
              >
                <div className="mt-1">{icon}</div>
                <div>
                  <p className="text-sm text-gray-600 font-semibold">{label}</p>
                  {key === "verificationStatus" ? (
                    getStatusBadge(value)
                  ) : (
                    <p className="text-gray-800 text-sm">
                      {renderValue(value)}
                    </p>
                  )}
                </div>
              </div>
            );
          }
        )}
      </div>
    </div>
  );
};

export const BankDetailsCard = ({ bankDetails }) => {
  const {
    bankName,
    accountHolderName,
    accountNumber,
    confirmAccountNumber,
    ifscCode,
    branchName,
    accountType,
    verificationStatus,
    verificationBy,
    verificationRemarks,
  } = bankDetails || {};

  const getStatusBadge = (status) => {
    switch (status) {
      case "verified":
        return (
          <span className="bg-green-100 text-green-700 px-3 py-1 text-xs rounded-full flex items-center gap-1">
            <ShieldCheck className="w-4 h-4" /> Verified
          </span>
        );
      case "pending":
        return (
          <span className="bg-yellow-100 text-yellow-700 px-3 py-1 text-xs rounded-full flex items-center gap-1">
            <Clock className="w-4 h-4" /> Pending
          </span>
        );
      case "rejected":
        return (
          <span className="bg-red-100 text-red-700 px-3 py-1 text-xs rounded-full flex items-center gap-1">
            <ShieldX className="w-4 h-4" /> Rejected
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-md p-6 mb-6 border">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
          <Banknote className="w-6 h-6 text-indigo-600" />
          Bank Details
        </h2>
        {getStatusBadge(verificationStatus)}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700 text-sm">
        <div className="flex items-center gap-3">
          <Landmark className="w-5 h-5 text-indigo-500" />
          <span>
            <strong>Bank Name:</strong> {bankName}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <User className="w-5 h-5 text-indigo-500" />
          <span>
            <strong>Account Holder:</strong> {accountHolderName}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Hash className="w-5 h-5 text-indigo-500" />
          <span>
            <strong>Account Number:</strong> {accountNumber}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Hash className="w-5 h-5 text-indigo-500" />
          <span>
            <strong>Confirm Account Number:</strong> {confirmAccountNumber}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Landmark className="w-5 h-5 text-indigo-500" />
          <span>
            <strong>IFSC Code:</strong> {ifscCode}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Landmark className="w-5 h-5 text-indigo-500" />
          <span>
            <strong>Branch:</strong> {branchName}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <FileCheck2 className="w-5 h-5 text-indigo-500" />
          <span>
            <strong>Account Type:</strong> {accountType}
          </span>
        </div>
        {verificationBy?.userName && (
          <div className="flex items-center gap-3 col-span-1 md:col-span-2">
            <ShieldCheck className="w-5 h-5 text-indigo-500" />
            <span>
              <strong>Verified By:</strong> {verificationBy.userName} (
              {verificationBy.employeeCode})
            </span>
          </div>
        )}
        {verificationRemarks && (
          <div className="col-span-1 md:col-span-2 text-gray-600 mt-2">
            <strong>Remarks:</strong> {verificationRemarks}
          </div>
        )}
      </div>
    </div>
  );
};

export const FamilyDetailsCard = ({ familyDetails }) => {
  if (!familyDetails) return null;

  const {
    fatherName,
    motherName,
    spouseName,
    numberOfSiblings,
    maritalStatus,
    emergencyContactName,
    emergencyContactRelation,
    emergencyContactNumber,
    permanentAddress,
  } = familyDetails;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6 mb-6 transition-all hover:shadow-xl">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
          <Users className="w-6 h-6 text-pink-500" />
          Family Details
        </h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 text-gray-700 text-[15px] leading-relaxed">
        {fatherName && (
          <div className="flex items-start gap-3">
            <UserPlus className="w-5 h-5 text-rose-500 mt-1" />
            <span>
              <strong>Father's Name:</strong> {fatherName}
            </span>
          </div>
        )}
        {motherName && (
          <div className="flex items-start gap-3">
            <UserPlus className="w-5 h-5 text-violet-500 mt-1" />
            <span>
              <strong>Mother's Name:</strong> {motherName}
            </span>
          </div>
        )}
        {spouseName && (
          <div className="flex items-start gap-3">
            <Heart className="w-5 h-5 text-red-500 mt-1" />
            <span>
              <strong>Spouse Name:</strong> {spouseName}
            </span>
          </div>
        )}
        {maritalStatus && (
          <div className="flex items-start gap-3">
            <Heart className="w-5 h-5 text-indigo-500 mt-1" />
            <span>
              <strong>Marital Status:</strong> {maritalStatus}
            </span>
          </div>
        )}
        {numberOfSiblings !== undefined && (
          <div className="flex items-start gap-3">
            <Users className="w-5 h-5 text-blue-500 mt-1" />
            <span>
              <strong>No. of Siblings:</strong> {numberOfSiblings}
            </span>
          </div>
        )}
        {emergencyContactName && (
          <div className="flex items-start gap-3">
            <Briefcase className="w-5 h-5 text-orange-500 mt-1" />
            <span>
              <strong>Emergency Contact:</strong> {emergencyContactName}
            </span>
          </div>
        )}
        {emergencyContactRelation && (
          <div className="flex items-start gap-3">
            <Users className="w-5 h-5 text-cyan-500 mt-1" />
            <span>
              <strong>Relationship:</strong> {emergencyContactRelation}
            </span>
          </div>
        )}
        {emergencyContactNumber && (
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-teal-500 mt-1" />
            <span>
              <strong>Emergency Contact No.:</strong> {emergencyContactNumber}
            </span>
          </div>
        )}
        {permanentAddress && (
          <div className="flex items-start gap-3 col-span-1 sm:col-span-2">
            <Home className="w-5 h-5 text-green-500 mt-1" />
            <span>
              <strong>Permanent Address:</strong> {permanentAddress}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export const LeaveBalanceCard = ({ leaveBalance }) => {
  if (!leaveBalance) return null;

  const {
    casualLeave,
    sickLeave,
    earnedLeave,
    paidLeave,
    maternityLeave,
    paternityLeave,
  } = leaveBalance;

  const leaveTypes = [
    {
      label: "Casual Leave",
      value: casualLeave,
      icon: <Sun className="w-5 h-5 text-yellow-500" />,
    },
    {
      label: "Sick Leave",
      value: sickLeave,
      icon: <BedDouble className="w-5 h-5 text-red-400" />,
    },
    {
      label: "Earned Leave",
      value: earnedLeave,
      icon: <Briefcase className="w-5 h-5 text-indigo-500" />,
    },
    {
      label: "Paid Leave",
      value: paidLeave,
      icon: <Umbrella className="w-5 h-5 text-green-500" />,
    },
    {
      label: "Maternity Leave",
      value: maternityLeave,
      icon: <ShieldCheck className="w-5 h-5 text-pink-500" />,
    },
    {
      label: "Paternity Leave",
      value: paternityLeave,
      icon: <ShieldCheck className="w-5 h-5 text-blue-500" />,
    },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-md hover:shadow-lg transition-all mb-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <CalendarCheck className="w-6 h-6 text-teal-600" />
        Leave Balance
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 text-[15px] text-gray-700">
        {leaveTypes.map(
          ({ label, value, icon }) =>
            value !== undefined && (
              <div
                key={label}
                className="flex items-start gap-3 bg-gray-50 px-4 py-3 rounded-xl border border-gray-100"
              >
                {icon}
                <div>
                  <p className="font-medium">{label}</p>
                  <p className="text-sm text-gray-600">{value} days</p>
                </div>
              </div>
            )
        )}
      </div>
    </div>
  );
};

const PopupWithUrl = ({ url, onClose, applicationObjId }) => {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 z-50">
      <div className="bg-white rounded-2xl w-[90%] h-[90%] shadow-xl relative">
        <div className="p-3 bg-gray-100 rounded-t-2xl text-center font-semibold text-gray-800">
          <Link
            className="absolute left-3 top-3 text-blue-600 underline"
            to={`/applications/${applicationObjId}/manage`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Go to Full Page
          </Link>
          Document Preview
          <button
            onClick={onClose}
            className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full"
          >
            ✕
          </button>
        </div>
        <iframe
          src={url}
          title="Dynamic Page"
          className="w-full h-full rounded-2xl"
        />
      </div>
    </div>
  );
};